import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { MAIN_BORDER_COLOR } from "components/theme";
import ActivityForm from "./activity-form";
import MarkdownContent from "./markdown-content";

const getActivityQuotes = (activity, quotes) => {
  if (Array.isArray(activity?.quotes) && activity.quotes.length) return activity.quotes;
  const quoteIds = Array.isArray(activity?.quote_ids) ? activity.quote_ids : [];
  return quotes.filter((quote) => quoteIds.includes(quote.id));
};

const quoteLabel = (quote) => `${quote.code} — ${quote.title}`;

export default function ActivityAccordion({
  activity,
  checking = false,
  checkResult = null,
  mode,
  editing = false,
  quotes = [],
  saving = false,
  onEdit,
  onDelete,
  onSave,
  onCancel,
}) {
  const [expanded, setExpanded] = useState(false);
  const activityQuotes = getActivityQuotes(activity, quotes);
  const open = editing || expanded;
  const handleAction = (callback) => (event) => {
    event.stopPropagation();
    callback?.(activity);
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={open}
      onChange={(_, isExpanded) => !editing && setExpanded(isExpanded)}
      sx={{
        border: `1px solid ${MAIN_BORDER_COLOR}`,
        marginBottom: "8px",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        sx={{ borderBottom: open ? `1px solid ${MAIN_BORDER_COLOR}` : "none" }}
      >
        <Box sx={{ alignItems: "center", display: "flex", flexGrow: 1, gap: 1, minWidth: 0 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ alignItems: "baseline", display: "flex", gap: 1, minWidth: 0 }}>
              <Typography component="span">{activity.date}</Typography>
              {mode === "admin" && activity.employee?.name ? (
                <Typography component="span" color="text.secondary">
                  {activity.employee.name}
                </Typography>
              ) : null}
            </Box>
            {activityQuotes.length ? (
              <Typography color="text.secondary" variant="body2">
                {activityQuotes.map(quoteLabel).join(", ")}
              </Typography>
            ) : null}
          </Box>
          {!editing && activity.can_edit ? (
            <IconButton aria-label="Modifica" onClick={handleAction(onEdit)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          ) : null}
          {!editing && activity.can_delete ? (
            <IconButton aria-label="Elimina" onClick={handleAction(onDelete)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {editing ? (
          <ActivityForm
            activity={activity}
            checking={checking}
            checkResult={checkResult}
            date={activity.date}
            quotes={quotes}
            saving={saving}
            onSave={onSave}
            onCancel={onCancel}
          />
        ) : (
          <MarkdownContent source={activity.description} />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
